const STORAGE_KEY = "fyt:stale-deployment-reload";

/**
 * Détection des erreurs causées par un onglet resté ouvert avant un déploiement.
 *
 * Deux signaux, du plus précis au plus général :
 *
 * 1. La stack pointe des chunks d'un **autre déploiement** que celui servi
 *    actuellement (paramètre `?dpl=` posé par Vercel sur chaque script). C'est
 *    le signal fiable : quel que soit le message, un bundle d'une autre version
 *    ne peut pas fonctionner avec le serveur courant.
 * 2. Le message d'erreur Next sur une Server Action disparue.
 *
 * Vécu en production le 2026-09-02 : trois alertes en une journée (« Server
 * Action … was not found », puis deux fois « e[o] is not a function » sur la
 * page d'accueil, dont une visiteuse Android) — toutes avec des chunks du
 * déploiement précédent.
 */

/** Extrait les identifiants de déploiement (`?dpl=…`) cités dans une stack. */
export function deploymentIdsInStack(stack: string): string[] {
  const ids = new Set<string>();
  for (const m of (stack ?? "").matchAll(/[?&]dpl=(dpl_[A-Za-z0-9]+)/g)) ids.add(m[1]);
  return [...ids];
}

/**
 * Vrai si la stack ne référence que des déploiements différents du courant.
 * Pur (testable) : l'appelant fournit le déploiement servi.
 */
export function referencesOtherDeployment(stack: string, currentId: string | null): boolean {
  if (!currentId) return false;
  const ids = deploymentIdsInStack(stack);
  return ids.length > 0 && ids.every((id) => id !== currentId);
}

/** Identifiant du déploiement qui a servi les scripts de la page courante. */
export function loadedDeploymentId(): string | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector<HTMLScriptElement>('script[src*="dpl=dpl_"]');
  if (!el) return null;
  try {
    return new URL(el.src, window.location.origin).searchParams.get("dpl");
  } catch {
    return null;
  }
}

export function isStaleDeploymentError(error: {
  message?: string;
  name?: string;
  stack?: string;
}): boolean {
  const msg = error?.message ?? "";
  if (
    msg.includes("Failed to find Server Action") ||
    msg.includes("was not found on the server") ||
    error?.name === "UnrecognizedActionError"
  ) {
    return true;
  }
  return referencesOtherDeployment(error?.stack ?? "", loadedDeploymentId());
}

/**
 * Recharge la page depuis le serveur pour récupérer le bundle à jour.
 *
 * Une seule fois par session : si l'erreur persiste après le rechargement,
 * c'est autre chose et il faut afficher l'écran d'erreur plutôt que boucler.
 * Renvoie `true` si un rechargement a été déclenché.
 */
export function reloadOnceForStaleDeployment(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY)) return false;
    window.sessionStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // sessionStorage indisponible (navigation privée, cookies bloqués) : on ne
    // recharge pas, faute de pouvoir se prémunir d'une boucle.
    return false;
  }
  window.location.reload();
  return true;
}

/** À appeler quand une page se rend correctement : réarme le garde-fou. */
export function clearStaleDeploymentFlag(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}
